import React from 'react';
import DateTimePicker from '../../ui/date-time-picker.ui';
import { UserRole } from '../../user/user.type';
import logo from '../../media/logo.png';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import AppStoreQRCode from '../../media/app_store_qr_code.png';
import GooglePlayQRCode from '../../media/google_play_qr_code.png';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import LoaderDots from '../../ui/loader-dots.ui';
import { Link } from 'react-router-dom';
import { FounderAgreementStatus } from '../dashboard.container';
import classNames from 'classnames';

import styles from './dashboard.module.css';

enum StoreLink {
  AppStore = 'https://apps.apple.com/ie/app/foundermatcha/id6502861320',
  GooglePlay = 'https://play.google.com/store/apps/details?id=com.foundermatcha.foundermatcha&hl=en_GB',
}

type DashboardUiProps = {
  preferredTime: dayjs.Dayjs | null;
  alternativeTime: dayjs.Dayjs | null;
  userRole: UserRole | undefined;
  isMeetingInviteSent: boolean;
  hasAppliedToJob: boolean;
  meetingInviteLoading?: boolean;
  inviteError?: string | null;
  founderAgreementStatus: FounderAgreementStatus;
  setFounderAgreementStatus: (status: FounderAgreementStatus) => void;
  setPreferredTime: (date: dayjs.Dayjs | null) => void;
  setAlternativeTime: (date: dayjs.Dayjs | null) => void;
  onLogout: () => void;
  onCreateInvite: () => void;
}

const DashboardUi = (props: DashboardUiProps) => {
  const {
    preferredTime,
    alternativeTime,
    userRole,
    meetingInviteLoading,
    isMeetingInviteSent,
    hasAppliedToJob,
    inviteError,
    founderAgreementStatus,
    setFounderAgreementStatus,
    setPreferredTime,
    setAlternativeTime,
    onLogout,
    onCreateInvite,
  } = props;

  return (
    <div className={styles.layout}>
      <div className={styles.container}>
        <div className={styles.textWrapper}>
          <img
            src={logo}
            alt="Foundermatcha Logo"
            className={styles.logo}
          />
          <button
            className={styles.signOutButton}
            onClick={onLogout}
          >
            Sign Out
          </button>
          {userRole === UserRole.Founder && (
            <>
              <div className={styles.title}>How does Foundermatcha work?</div>
              <div className={styles.text}>
                Foundermatcha helps you find compatible engineers to support you on your startup journey. Whether you’re looking for a co-founder, freelancer, advisor, or anything in between, Foundermatcha connects you with the right technical partners.
              </div>
              <div className={styles.text}>
                We’ll give you your first engineer match and meeting at no upfront cost.
              </div>
              <div className={styles.text}>
                If you then decide to collaborate with that engineer on your startup in any capacity (co-founder, freelancer, advisor or similar), you agree to retroactively purchase the Foundermatcha Pro annual subscription.
              </div>
              <div className={styles.title}>
                Foundermatcha Pro
              </div>
              <div className={styles.text}>
                Foundermatcha Pro is an annual subscription that lets you meet and chat with compatible engineers as much as you like. There’s no limit on how many people you can bring into your startup or in what capacity.
              </div>
              <div className={styles.text}>
                The subscription is available at <span className={styles.boldText}>£39 per month</span> or a <span className={styles.boldText}>£399 one-off annual payment</span>.
              </div>
              <div className={styles.title}>
                Do you agree to proceed on this basis?
              </div>
              <div className={styles.buttonWrapper}>
                <button
                  className={styles.button}
                  onClick={() => setFounderAgreementStatus(FounderAgreementStatus.Agreed)}
                >
                  <CheckCircleOutlineIcon />
                  Yes, I agree
                </button>
                <button
                  className={classNames(
                    styles.button,
                    styles.rejectButton,
                  )}
                  onClick={() => setFounderAgreementStatus(FounderAgreementStatus.Declined)}
                >
                  <CloseIcon />
                  No, I don't agree
                </button>
              </div>
            </>
          )}
          {founderAgreementStatus === FounderAgreementStatus.Agreed && (
            <>
              <div className={styles.title}>Thank you!</div>
              <div className={styles.text}>
                We are searching our networks now for compatible and available engineers for you to meet.
              </div>
              <div className={styles.text}>
                Keep an eye out for our email in your inbox. If it has not arrived in 15 minutes, please check your spam.
              </div>
              <div className={styles.text}>
                For any questions in the meantime, email us at hello@foundermatcha.com
              </div>
            </>
          )}
          {founderAgreementStatus === FounderAgreementStatus.Declined && (
            <>
              <div className={styles.title}>Thank you for your feedback!</div>
              <div className={styles.text}>
                We appreciate you letting us know your decision regarding the Pro Agreement. We understand, and we are still happy to have you on the platform!                  </div>
              <div className={styles.text}>
                We are searching our networks now for compatible and available engineers for you to meet under the terms of the free tier.
              </div>
              <div className={styles.text}>
                Keep an eye out for our email in your inbox. It will outline your free credit and access limits for the free tier. If it has not arrived in 15 minutes, please check your spam.
              </div>
              <div className={styles.text}>
                For any questions in the meantime, email us at hello@foundermatcha.com
              </div>
            </>
          )}
          {userRole === UserRole.SoftwareEngineer && (
            hasAppliedToJob && !isMeetingInviteSent ? (
              <>
                <div className={styles.title}>Set Your Availability For A Meeting With the Founder</div>
                <div className={styles.subTitle}>
                  Once the founder expresses interest in your profile, we'll use your selected time slots below to schedule the introductory meeting.
                </div>
                <div className={styles.subTitle}>
                  If none of your preferred times work, the founder may propose an alternative.
                </div>
                <div className={styles.subTitle}>
                  Tip: Please choose at least 2–3 days from today to allow time for coordination.
                </div>

                <div className={styles.meetingSection}>
                  <div className={styles.dateTimeContainer}>
                    <div className={styles.meetingSlot}>
                      <div className={styles.slotTitle}>Preferred Time</div>
                      <DateTimePicker
                        value={preferredTime}
                        onChange={setPreferredTime}
                        disabled={meetingInviteLoading}
                      />
                    </div>

                    <div className={styles.meetingSlot}>
                      <div className={styles.slotTitle}>Alternative Time</div>
                      <DateTimePicker
                        value={alternativeTime}
                        onChange={setAlternativeTime}
                        disabled={meetingInviteLoading}
                      />
                    </div>
                    {alternativeTime && preferredTime && alternativeTime.isSame(preferredTime) && (
                      <div className={styles.error}>
                        Please select two different meeting times
                      </div>
                    )}
                  </div>

                  <button
                    className={styles.button}
                    onClick={onCreateInvite}
                    disabled={!preferredTime || !alternativeTime || preferredTime.isSame(alternativeTime) || meetingInviteLoading}
                  >
                    {meetingInviteLoading ? (
                      <div className={styles.loader}>
                        <LoaderDots />
                      </div>
                    ) : (
                      <>
                        <CalendarMonthOutlinedIcon />
                        Send an Invite
                      </>
                    )}
                  </button>
                  {inviteError && (
                    <div className={styles.error}>
                      {inviteError}
                    </div>
                  )}
                </div>
              </>
            ) : hasAppliedToJob && isMeetingInviteSent ? (
              <>
                <div className={styles.title}>Thank You!</div>
                <div className={styles.subTitle}>
                  Your meeting invite has been sent successfully.
                </div>
                <div className={styles.title}>What Happens Next</div>
                <div className={styles.subTitle}>
                  If the founder is interested in your profile, a meeting will be scheduled based on the times you've selected above.
                </div>
                <div className={styles.subTitle}>
                  The meeting will take place directly within the <span className={styles.boldText}>Foundermatcha app</span>, so please ensure you <span className={styles.boldText}>download the app before your meeting</span> to join and chat seamlessly.
                </div>
                <div className={styles.qrCodesWrapper}>
                  <div className={styles.qrCodeContainer}>
                    <div className={styles.qrLabel}>
                      <a href={StoreLink.AppStore} target="_blank" rel="noopener noreferrer">
                        <div className={styles.qrSubLabel}>App Store</div>
                      </a>
                    </div>
                    <img
                      src={AppStoreQRCode}
                      alt="Download on the App Store"
                      className={styles.qrImage}
                    />
                  </div>

                  <div className={styles.qrCodeContainer}>
                    <div className={styles.qrLabel}>
                      <a href={StoreLink.GooglePlay} target="_blank" rel="noopener noreferrer">
                        <div className={styles.qrSubLabel}>Google Play</div>
                      </a>
                    </div>
                    <img
                      src={GooglePlayQRCode}
                      alt="Get it on Google Play"
                      className={styles.qrImage}
                    />
                  </div>
                </div>
              </>
            ) : null
          )}
          {userRole === UserRole.SoftwareEngineer && !hasAppliedToJob && (
            <>
              <div className={styles.title}>Welcome!</div>
              <div className={styles.text}>
                Your account has been successfully created.
              </div>
              <div className={styles.title}>Download the app now:</div>
              <div className={styles.qrCodesWrapper}>
                <div className={styles.qrCodeContainer}>
                  <div className={styles.qrLabel}>
                    <a href={StoreLink.AppStore} target="_blank" rel="noopener noreferrer">
                      <div className={styles.qrSubLabel}>App Store</div>
                    </a>
                  </div>
                  <img
                    src={AppStoreQRCode}
                    alt="Download on the App Store"
                    className={styles.qrImage}
                  />
                </div>

                <div className={styles.qrCodeContainer}>
                  <div className={styles.qrLabel}>
                    <a href={StoreLink.GooglePlay} target="_blank" rel="noopener noreferrer">
                      <div className={styles.qrSubLabel}>Google Play</div>
                    </a>
                  </div>
                  <img
                    src={GooglePlayQRCode}
                    alt="Get it on Google Play"
                    className={styles.qrImage}
                  />
                </div>
              </div>
              <div
                className={classNames(
                  styles.text,
                  styles.greenText,
                )}
              >
                If you want to learn more about Foundermatcha, explore our
                <br/>
                <Link
                  to="https://www.foundermatcha.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  website
                </Link>.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardUi;
